import { prefersReducedMotion } from "../utils/reduced-motion";
import { initLazyMedia } from "../utils/lazy-media";

/**
 * Randomize sense gallery layout and sizes
 * Uses CSS columns for native masonry effect
 * Implements lazy loading: initial 20 items, then loads 20 more automatically
 */
export function initSenseGallery() {
  const gallery = document.querySelector('.c-sense-gallery');
  if (!gallery) return;

  const items = Array.from(gallery.querySelectorAll('.c-sense-gallery__item'));
  if (items.length === 0) return;

  // Check if we're on a medium+ screen (where randomization applies)
  const isMediumScreen = window.matchMedia('(min-width: 768px)').matches;
  
  // Check if randomization is enabled via data attribute
  const randomizeSort = gallery.getAttribute('data-randomize-sort') === 'true';
  
  // Process initial items
  processItems(items, gallery, isMediumScreen, randomizeSort);

  // Set up lazy loading for remaining items
  const remainingDataAttr = gallery.getAttribute('data-remaining-items');
  if (remainingDataAttr) {
    try {
      const remainingItems = JSON.parse(remainingDataAttr);
      if (remainingItems.length > 0) {
        setupLazyLoading(gallery, remainingItems, isMediumScreen, randomizeSort);
      }
    } catch (e) {
      console.error('Error parsing remaining items data:', e);
    }
  }

  initLazyMedia(gallery);
}

/**
 * Process items: randomize order and sizes
 */
function processItems(items, gallery, isMediumScreen, randomizeSort) {
  if (items.length === 0) return;

  // On mobile, just use default single column (no randomization)
  if (!isMediumScreen) {
    return;
  }

  // Only shuffle items if randomization is enabled
  let processedItems = [...items];
  if (randomizeSort) {
    processedItems = processedItems.sort(() => Math.random() - 0.5);
  }
  
  // Remove items from DOM
  processedItems.forEach((item) => {
    item.remove();
  });

  // Randomize item sizes (1/3 or 2/3 width)
  // Mix of sizes: ~85% small (1/3), ~15% large (2/3) - large items span all columns
  processedItems.forEach((item) => {
    const size = Math.random() < 0.85 ? 'size-1-3' : 'size-2-3';
    item.classList.add(size);
  });

  // Re-append items - CSS columns will handle the masonry layout
  processedItems.forEach((item) => {
    gallery.appendChild(item);
  });
}

/**
 * Set up lazy loading with Intersection Observer
 */
function setupLazyLoading(gallery, remainingItems, isMediumScreen, randomizeSort) {
  const batchSize = 20;
  let loadedCount = 0;
  let isLoading = false;

  // Create a sentinel element at the bottom of the gallery
  const sentinel = document.createElement('div');
  sentinel.className = 'c-sense-gallery__sentinel';
  sentinel.style.height = '1px';
  sentinel.style.width = '100%';
  gallery.appendChild(sentinel);

  // Intersection Observer to detect when sentinel is visible
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isLoading && loadedCount < remainingItems.length) {
          loadNextBatch();
        }
      });
    },
    {
      rootMargin: '200px', // Start loading when sentinel is 200px away from viewport
      threshold: 0.1,
    }
  );

  observer.observe(sentinel);

  /**
   * Load next batch of items
   */
  function loadNextBatch() {
    if (isLoading || loadedCount >= remainingItems.length) return;
    
    isLoading = true;
    
    // Get next batch
    const batch = remainingItems.slice(loadedCount, loadedCount + batchSize);
    const newItems = [];

    // Create DOM elements for each item in batch
    batch.forEach((fileData) => {
      const item = createGalleryItem(fileData);
      newItems.push(item);
    });

    // Process and append items
    if (newItems.length > 0) {
      // Remove sentinel temporarily
      sentinel.remove();

      // Process items (randomize if needed)
      processItems(newItems, gallery, isMediumScreen, randomizeSort);

      // Re-add sentinel at the end
      gallery.appendChild(sentinel);

      initLazyMedia(gallery);

      loadedCount += batch.length;
    }

    isLoading = false;

    // If all items are loaded, disconnect observer
    if (loadedCount >= remainingItems.length) {
      observer.disconnect();
      sentinel.remove();
    }
  }
}

/**
 * Create a gallery item DOM element from file data
 */
function createGalleryItem(fileData) {
  const item = document.createElement('div');
  item.className = 'c-sense-gallery__item';
  item.setAttribute('data-file-type', fileData.type);

  if (fileData.type === 'video') {
    const video = document.createElement('video');
    const reducedMotion = prefersReducedMotion();

    if (!reducedMotion) {
      video.setAttribute('autoplay', 'true');
      video.setAttribute('data-autoplay', 'true');
    } else {
      video.setAttribute('controls', 'true');
    }
    video.setAttribute('loop', 'true');
    video.setAttribute('muted', 'true');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('preload', 'none');
    video.className = 'o-video o-video-container-fit o-video-container-fit--landscape lazyload';
    video.setAttribute('data-object-fit', 'cover');
    
    const source = document.createElement('source');
    source.setAttribute('type', 'video/mp4');
    source.setAttribute('src', fileData.url);
    video.appendChild(source);
    
    item.appendChild(video);
  } else {
    // Create image element
    const figure = document.createElement('figure');
    
    const imageContainer = document.createElement('div');
    imageContainer.className = 'm-image-container';
    imageContainer.setAttribute('data-ratio', fileData.ratio || '');
    imageContainer.setAttribute('data-crop', 'false');
    
    // Placeholder
    const placeholder = document.createElement('img');
    placeholder.src = fileData.placeholder;
    placeholder.className = 'm-placeholder';
    placeholder.alt = '';
    imageContainer.appendChild(placeholder);
    
    // Main image
    const img = document.createElement('img');
    img.setAttribute('data-src', fileData.thumb);
    img.setAttribute('src', 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==');
    img.setAttribute('data-srcset', fileData.srcset || '');
    img.setAttribute('data-sizes', 'auto');
    img.setAttribute('data-ratio', fileData.ratio || '');
    img.setAttribute('alt', fileData.alt || '');
    img.className = 'o-image lazyload';
    
    if (fileData.focusX !== undefined && fileData.focusY !== undefined) {
      img.style.objectFit = 'cover';
      img.style.objectPosition = `${fileData.focusX}% ${fileData.focusY}%`;
    }
    
    imageContainer.appendChild(img);
    figure.appendChild(imageContainer);
    
    // Caption
    if (fileData.caption) {
      const caption = document.createElement('figcaption');
      caption.className = 't-s';
      caption.innerHTML = fileData.caption;
      figure.appendChild(caption);
    }
    
    item.appendChild(figure);
  }

  return item;
}
